import { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Plus, UserPlus, UserMinus, Search } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { useLanguage } from '../hooks/useLanguage';
import { useToast } from '../hooks/useToast';
import {
  getCalendarEvents,
  getEventLeads,
  getActivities,
  createEvent,
  updateEvent,
  markAttendance,
  updateLead,
  getLeads
} from '../services/api';

export const CalendarView = () => {
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsModalOpen, setEventDetailsModalOpen] = useState(false);
  const [eventFormModalOpen, setEventFormModalOpen] = useState(false);
  const [eventLeads, setEventLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [defaultView, setDefaultView] = useState('dayGridMonth');
  const [businessHours, setBusinessHours] = useState([]);
  const [slotMinTime, setSlotMinTime] = useState('00:00:00');
  const [slotMaxTime, setSlotMaxTime] = useState('24:00:00');
  const { t, language } = useLanguage();
  const toast = useToast();

  const [formData, setFormData] = useState({
    event_name: '',
    event_date: '',
    event_end_date: '',
    activity_id: '',
    capacity: 0,
    is_private: false,
    recurrence_type: 'none',
    recurrence_end_date: '',
  });

  // Add lead modal states
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingAvailableLeads, setLoadingAvailableLeads] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [selectedLeadToAdd, setSelectedLeadToAdd] = useState('');

  useEffect(() => {
    fetchEventsAndActivities();
  }, []);

  // Load default calendar view and business hours from settings
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('activity_schedule_settings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);

          // Load default view
          if (settings.defaultCalendarView) {
            setDefaultView(settings.defaultCalendarView);
          }

          // Load business hours
          if (settings.workingDays) {
            const businessHoursArray = [];
            const dayMap = {
              sunday: 0,
              monday: 1,
              tuesday: 2,
              wednesday: 3,
              thursday: 4,
              friday: 5,
              saturday: 6,
            };

            let earliestStart = '23:59';
            let latestEnd = '00:00';

            Object.entries(settings.workingDays).forEach(([day, config]) => {
              if (config.enabled) {
                businessHoursArray.push({
                  daysOfWeek: [dayMap[day]],
                  startTime: config.startTime,
                  endTime: config.endTime,
                });

                // Track earliest and latest times
                if (config.startTime < earliestStart) {
                  earliestStart = config.startTime;
                }
                if (config.endTime > latestEnd) {
                  latestEnd = config.endTime;
                }
              }
            });

            setBusinessHours(businessHoursArray);

            // Set slot times (add :00 for seconds)
            if (businessHoursArray.length > 0) {
              setSlotMinTime(earliestStart + ':00');
              setSlotMaxTime(latestEnd + ':00');
            }
          }
        }
      } catch (error) {
        console.error('Failed to load calendar settings:', error);
      }
    };

    loadSettings();

    // Listen for settings updates
    const handleSettingsUpdate = (event) => {
      if (event.detail) {
        if (event.detail.defaultCalendarView) {
          setDefaultView(event.detail.defaultCalendarView);
        }

        if (event.detail.workingDays) {
          const businessHoursArray = [];
          const dayMap = {
            sunday: 0,
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
          };

          let earliestStart = '23:59';
          let latestEnd = '00:00';

          Object.entries(event.detail.workingDays).forEach(([day, config]) => {
            if (config.enabled) {
              businessHoursArray.push({
                daysOfWeek: [dayMap[day]],
                startTime: config.startTime,
                endTime: config.endTime,
              });

              // Track earliest and latest times
              if (config.startTime < earliestStart) {
                earliestStart = config.startTime;
              }
              if (config.endTime > latestEnd) {
                latestEnd = config.endTime;
              }
            }
          });

          setBusinessHours(businessHoursArray);

          // Set slot times (add :00 for seconds)
          if (businessHoursArray.length > 0) {
            setSlotMinTime(earliestStart + ':00');
            setSlotMaxTime(latestEnd + ':00');
          }
        }
      }
    };

    window.addEventListener('activityScheduleSettingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('activityScheduleSettingsUpdated', handleSettingsUpdate);
    };
  }, []);

  const fetchEventsAndActivities = async () => {
    try {
      setLoading(true);
      // Get date range for current view (3 months back and forward for performance)
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 4, 0);

      const [eventsData, activitiesData] = await Promise.all([
        getCalendarEvents({
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }),
        getActivities(true)
      ]);
      setEvents(eventsData);
      setActivities(activitiesData);
    } catch (error) {
      console.error('Failed to load events:', error);
      toast.error(t('failedToLoadEvents') || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (dateClickInfo) => {
    // Open create event modal with clicked date pre-filled
    const clickedDate = dateClickInfo.date;
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clickedDate.getDate()).padStart(2, '0');
    const hours = String(clickedDate.getHours() || 10).padStart(2, '0');
    const minutes = String(clickedDate.getMinutes() || 0).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

    // Default end time is 1 hour after start
    const endDate = new Date(clickedDate.getTime() + 60 * 60 * 1000);
    const endYear = endDate.getFullYear();
    const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDate.getDate()).padStart(2, '0');
    const endHours = String(endDate.getHours()).padStart(2, '0');
    const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
    const formattedEndDate = `${endYear}-${endMonth}-${endDay}T${endHours}:${endMinutes}`;

    setEditingEvent(null);
    setFormData({
      event_name: '',
      event_date: formattedDate,
      event_end_date: formattedEndDate,
      activity_id: '',
      capacity: 0,
      is_private: false,
      recurrence_type: 'none',
      recurrence_end_date: '',
    });
    setEventFormModalOpen(true);
  };

  const handleEventClick = async (clickInfo) => {
    const eventId = clickInfo.event.id;
    const eventData = events.find(e => e.id.toString() === eventId);

    if (eventData) {
      setSelectedEvent(eventData);
      setEventDetailsModalOpen(true);

      // Fetch leads for this event
      try {
        setLoadingLeads(true);
        const leadsData = await getEventLeads(eventId);
        const leads = Array.isArray(leadsData) ? leadsData : [];
        setEventLeads(leads);

        // Initialize attendance status from lead data
        const initialAttendanceStatus = {};
        leads.forEach(lead => {
          if (lead.attendance_status) {
            initialAttendanceStatus[lead.id] = lead.attendance_status;
          }
        });
        setAttendanceStatus(initialAttendanceStatus);
      } catch (error) {
        console.error('Failed to load event leads:', error);
        toast.error(t('failedToLoadLeads') || 'Failed to load event leads');
      } finally {
        setLoadingLeads(false);
      }
    }
  };

  const handleCloseEventDetails = () => {
    setEventDetailsModalOpen(false);
    setSelectedEvent(null);
    setEventLeads([]);
    setAttendanceStatus({});
  };

  const handleOpenEditEvent = (event) => {
    // Format date for datetime-local input
    const eventDate = new Date(event.event_date);
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

    // Format end date
    const eventEndDate = new Date(event.event_end_date);
    const endYear = eventEndDate.getFullYear();
    const endMonth = String(eventEndDate.getMonth() + 1).padStart(2, '0');
    const endDay = String(eventEndDate.getDate()).padStart(2, '0');
    const endHours = String(eventEndDate.getHours()).padStart(2, '0');
    const endMinutes = String(eventEndDate.getMinutes()).padStart(2, '0');
    const formattedEndDate = `${endYear}-${endMonth}-${endDay}T${endHours}:${endMinutes}`;

    setEditingEvent(event);
    setFormData({
      event_name: event.event_name,
      event_date: formattedDate,
      event_end_date: formattedEndDate,
      activity_id: event.activity_id || '',
      capacity: event.capacity || 0,
      is_private: event.is_private || false,
      recurrence_type: event.recurrence_type || 'none',
      recurrence_end_date: event.recurrence_end_date || '',
    });
    setEventDetailsModalOpen(false);
    setEventFormModalOpen(true);
  };

  const handleCloseEventForm = () => {
    setEventFormModalOpen(false);
    setEditingEvent(null);
    setFormData({
      event_name: '',
      event_date: '',
      event_end_date: '',
      activity_id: '',
      capacity: 0,
      is_private: false,
      recurrence_type: 'none',
      recurrence_end_date: '',
    });
  };

  const handleEventFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value),
    }));
  };

  const handleEventFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        event_date: new Date(formData.event_date).toISOString(),
        event_end_date: new Date(formData.event_end_date).toISOString(),
        activity_id: formData.activity_id ? parseInt(formData.activity_id) : null,
      };

      if (editingEvent) {
        await updateEvent(editingEvent.id, submitData);
        toast.success(t('eventUpdated') || 'Event updated successfully');
      } else {
        await createEvent(submitData);
        toast.success(t('eventCreated') || 'Event created successfully');
      }
      handleCloseEventForm();
      fetchEventsAndActivities();
    } catch (error) {
      toast.error(t('failedToSaveEvent') || 'Failed to save event');
    }
  };

  const handleAttendanceChange = async (leadId, newStatus) => {
    try {
      await markAttendance(leadId, selectedEvent.id, newStatus);

      setAttendanceStatus(prev => ({
        ...prev,
        [leadId]: newStatus
      }));

      // Update the lead in the list
      setEventLeads(prev => prev.map(lead =>
        lead.id === leadId ? { ...lead, attendance_status: newStatus } : lead
      ));

      toast.success(t('attendanceUpdated') || 'Attendance updated successfully');
    } catch (error) {
      console.error('Failed to update attendance:', error);
      toast.error(t('failedToUpdateAttendance') || 'Failed to update attendance');
    }
  };

  const handleUnassignLead = async (leadId) => {
    if (!confirm(t('confirmUnassignLead') || 'Are you sure you want to unassign this lead from the event?')) {
      return;
    }

    try {
      await updateLead(leadId, {
        event_id: null,
        action_date: null,
        current_event_id: selectedEvent.id
      });

      // Remove lead from the list
      setEventLeads(prev => prev.filter(lead => lead.id !== leadId));
      toast.success(t('leadUnassigned') || 'Lead unassigned from event successfully');

      // Refresh events to update counts
      fetchEventsAndActivities();
    } catch (error) {
      console.error('Failed to unassign lead:', error);
      const errorMsg = error.response?.data?.error?.code === 'CANNOT_UNASSIGN_ATTENDED'
        ? (t('cannotUnassignAttendedLead') || 'Cannot unassign lead who attended the event')
        : (t('failedToUnassignLead') || 'Failed to unassign lead from event');
      toast.error(errorMsg);
    }
  };

  const handleOpenAddLead = async () => {
    try {
      setLoadingAvailableLeads(true);
      setAddLeadModalOpen(true);
      const allLeads = await getLeads();

      // Filter out leads already assigned to this event
      const assignedLeadIds = eventLeads.map(lead => lead.id);
      const available = allLeads.filter(lead => !assignedLeadIds.includes(lead.id));
      setAvailableLeads(available);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error(t('failedToLoadLeads') || 'Failed to load leads');
    } finally {
      setLoadingAvailableLeads(false);
    }
  };

  const handleCloseAddLead = () => {
    setAddLeadModalOpen(false);
    setSelectedLeadToAdd('');
    setLeadSearchQuery('');
  };

  const handleAddLeadToEvent = async () => {
    if (!selectedLeadToAdd) {
      toast.error(t('pleaseSelectLead') || 'Please select a lead');
      return;
    }

    try {
      await updateLead(selectedLeadToAdd, {
        event_id: selectedEvent.id,
        action_date: selectedEvent.event_date
      });

      toast.success(t('leadAddedToEvent') || 'Lead added to event successfully');
      handleCloseAddLead();

      // Refresh event leads
      const leadsData = await getEventLeads(selectedEvent.id);
      setEventLeads(Array.isArray(leadsData) ? leadsData : []);

      // Refresh events to update counts
      fetchEventsAndActivities();
    } catch (error) {
      console.error('Failed to add lead to event:', error);
      toast.error(t('failedToAddLead') || 'Failed to add lead to event');
    }
  };

  const filteredAvailableLeads = availableLeads.filter(lead => {
    const searchLower = leadSearchQuery.toLowerCase();
    const name = lead.field_data?.full_name?.toLowerCase() || '';
    const phone = lead.field_data?.phone?.toLowerCase() || '';
    const email = lead.field_data?.email?.toLowerCase() || '';
    return name.includes(searchLower) || phone.includes(searchLower) || email.includes(searchLower);
  });

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const locale = language === 'he' ? 'he-IL' : 'en-US';

    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Convert events to FullCalendar format
  const calendarEvents = events.map(event => {
    const activity = activities.find(a => a.id === event.activity_id);
    const assignedCount = parseInt(event.assigned_count) || 0;
    const capacity = event.capacity || 0;
    const isFull = event.is_full || (capacity > 0 && assignedCount >= capacity);
    const isPast = event.is_past || new Date(event.event_date) < new Date();

    // Determine color based on status
    let backgroundColor = '#3b82f6'; // Default blue
    let borderColor = '#2563eb';

    if (isPast) {
      backgroundColor = '#6b7280'; // Gray for past events
      borderColor = '#4b5563';
    } else if (isFull) {
      backgroundColor = '#ef4444'; // Red for full events
      borderColor = '#dc2626';
    } else if (event.is_private) {
      backgroundColor = '#8b5cf6'; // Purple for private events
      borderColor = '#7c3aed';
    }

    return {
      id: event.id.toString(),
      title: event.event_name,
      start: event.event_date,
      end: event.event_end_date,
      backgroundColor,
      borderColor,
      extendedProps: {
        activity: activity?.activity_name,
        assigned: assignedCount,
        capacity: capacity,
        isPrivate: event.is_private,
        isFull,
        isPast
      }
    };
  });

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('calendar') || 'Calendar'}</h2>
          <p className="text-gray-600 mt-1">
            {t('viewEventsCalendar') || 'View all events in calendar format'}
          </p>
        </div>
        <Button onClick={() => handleDateClick({ date: new Date() })}>
          <Plus className="w-4 h-4 mr-2" />
          {t('addEvent') || 'Add Event'}
        </Button>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span>{t('upcomingEvents') || 'Upcoming'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-500 rounded"></div>
          <span>{t('privateEvents') || 'Private'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span>{t('fullEvents') || 'Full'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-500 rounded"></div>
          <span>{t('pastEvents') || 'Past'}</span>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white shadow-md rounded-lg p-4">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={defaultView}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          height="auto"
          locale={language === 'he' ? 'he' : 'en'}
          direction={language === 'he' ? 'rtl' : 'ltr'}
          firstDay={0} // Sunday
          businessHours={businessHours.length > 0 ? businessHours : undefined}
          selectConstraint="businessHours"
          eventConstraint="businessHours"
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          buttonText={{
            today: t('today') || 'Today',
            month: t('month') || 'Month',
            week: t('week') || 'Week',
            day: t('day') || 'Day'
          }}
          eventContent={(eventInfo) => {
            return (
              <div className="p-1 overflow-hidden">
                <div className="font-semibold text-xs truncate">{eventInfo.event.title}</div>
                <div className="text-xs">
                  {eventInfo.event.extendedProps.assigned}
                  {eventInfo.event.extendedProps.capacity > 0 ? `/${eventInfo.event.extendedProps.capacity}` : ''}
                </div>
              </div>
            );
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
        />
      </div>

      {/* Event Form Modal */}
      <Modal
        isOpen={eventFormModalOpen}
        onClose={handleCloseEventForm}
        title={editingEvent ? (t('editEvent') || 'Edit Event') : (t('addEvent') || 'Add Event')}
      >
        <form onSubmit={handleEventFormSubmit} className="space-y-4">
          <Input
            label={t('eventName') || 'Event Name'}
            name="event_name"
            value={formData.event_name}
            onChange={handleEventFormChange}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('eventStartDate') || 'Start Date & Time'}
            </label>
            <input
              type="datetime-local"
              name="event_date"
              value={formData.event_date}
              onChange={handleEventFormChange}
              step="1800"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('eventEndDate') || 'End Date & Time'}
            </label>
            <input
              type="datetime-local"
              name="event_end_date"
              value={formData.event_end_date}
              onChange={handleEventFormChange}
              step="1800"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('activityCategory') || 'Activity Category'}
            </label>
            <select
              name="activity_id"
              value={formData.activity_id}
              onChange={handleEventFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('selectActivity') || 'Select Activity...'}</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.activity_name}
                </option>
              ))}
            </select>
          </div>

          <Input
            label={t('capacity') || 'Capacity'}
            type="number"
            name="capacity"
            value={formData.capacity}
            onChange={handleEventFormChange}
            min="0"
            helpText={t('capacityHelp') || '0 = unlimited capacity'}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_private"
              name="is_private"
              checked={formData.is_private}
              onChange={handleEventFormChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_private" className="text-sm font-medium text-gray-700">
              {t('private') || 'Private Event'}
            </label>
          </div>

          {/* Recurrence Options */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{t('recurrence') || 'Recurrence'}</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('recurrenceType') || 'Repeat'}
              </label>
              <select
                name="recurrence_type"
                value={formData.recurrence_type}
                onChange={handleEventFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="none">{t('noRecurrence') || 'Does not repeat'}</option>
                <option value="daily">{t('daily') || 'Daily'}</option>
                <option value="weekly">{t('weekly') || 'Weekly'}</option>
                <option value="monthly">{t('monthly') || 'Monthly'}</option>
              </select>
            </div>

            {formData.recurrence_type !== 'none' && (
              <div className="mt-3">
                <Input
                  label={t('recurrenceEndDate') || 'Ends on'}
                  type="date"
                  name="recurrence_end_date"
                  value={formData.recurrence_end_date}
                  onChange={handleEventFormChange}
                  required={formData.recurrence_type !== 'none'}
                  helpText={t('recurrenceEndDateHelp') || 'Last date for recurring events'}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseEventForm}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button type="submit">
              {editingEvent ? (t('update') || 'Update') : (t('create') || 'Create')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Event Details Modal */}
      {selectedEvent && (
        <Modal
          isOpen={eventDetailsModalOpen}
          onClose={handleCloseEventDetails}
          title={selectedEvent.event_name}
        >
          <div className="space-y-4">
            {/* Event Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">{t('eventDate') || 'Date'}:</span>
                  <p className="font-medium">{formatDateTime(selectedEvent.event_date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('activityCategory') || 'Category'}:</span>
                  <p className="font-medium">
                    {selectedEvent.activity_id
                      ? (activities.find(a => a.id === selectedEvent.activity_id)?.activity_name || '-')
                      : '-'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">{t('capacity') || 'Capacity'}:</span>
                  <p className="font-medium">
                    {selectedEvent.capacity === 0 ? (t('unlimited') || 'Unlimited') : selectedEvent.capacity}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">{t('assigned') || 'Assigned'}:</span>
                  <p className="font-medium">{selectedEvent.assigned_count || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('visibility') || 'Visibility'}:</span>
                  <p className="font-medium">
                    {selectedEvent.is_private ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        🔒 {t('private') || 'Private'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        🌐 {t('public') || 'Public'}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleOpenEditEvent(selectedEvent)}
                >
                  {t('edit') || 'Edit'} {t('event') || 'Event'}
                </Button>
              </div>
            </div>

            {/* Assigned Leads with Attendance */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-900">
                  {t('assignedLeads') || 'Assigned Leads'} ({eventLeads.length})
                </h4>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleOpenAddLead}
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  {t('addLead') || 'Add Lead'}
                </Button>
              </div>
              {loadingLeads ? (
                <div className="text-center py-4 text-gray-500">{t('loading')}</div>
              ) : eventLeads.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  {t('noLeadsAssigned') || 'No leads assigned to this event'}
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {t('name') || 'Name'}
                        </th>
                        <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {t('phone') || 'Phone'}
                        </th>
                        <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {t('attendance') || 'Attendance'}
                        </th>
                        <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase ${language === 'he' ? 'text-right' : 'text-left'}`}>
                          {t('actions') || 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {eventLeads
                        .filter(lead => lead.attendance_status !== 'waiting_list')
                        .map((lead) => (
                        <tr key={lead.id}>
                          <td className={`px-4 py-2 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            {lead.field_data?.full_name || '-'}
                          </td>
                          <td className={`px-4 py-2 text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            {lead.field_data?.phone || '-'}
                          </td>
                          <td className={`px-4 py-2 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            <select
                              value={attendanceStatus[lead.id] || lead.attendance_status || ''}
                              onChange={(e) => handleAttendanceChange(lead.id, e.target.value)}
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">{t('pending') || 'Pending'}</option>
                              <option value="attended">{t('attended') || 'Attended'}</option>
                              <option value="absent">{t('absent') || 'Absent'}</option>
                            </select>
                          </td>
                          <td className={`px-4 py-2 text-sm ${language === 'he' ? 'text-right' : 'text-left'}`}>
                            <button
                              onClick={() => handleUnassignLead(lead.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                              title={t('unassign') || 'Unassign'}
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={handleCloseEventDetails}>
                {t('close') || 'Close'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Lead to Event Modal */}
      <Modal
        isOpen={addLeadModalOpen}
        onClose={handleCloseAddLead}
        title={t('addLeadToEvent') || 'Add Lead to Event'}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('addLeadToEventInfo') || 'Select a lead to add to this event.'}
          </p>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('searchByNamePhoneEmail') || 'Search by name, phone, or email...'}
              value={leadSearchQuery}
              onChange={(e) => setLeadSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Lead Selection */}
          {loadingAvailableLeads ? (
            <div className="text-center py-8 text-gray-500">{t('loading')}</div>
          ) : filteredAvailableLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {leadSearchQuery
                ? (t('noLeadsFound') || 'No leads found matching your search')
                : (t('noAvailableLeads') || 'No available leads without a group')}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
              <div className="divide-y divide-gray-200">
                {filteredAvailableLeads.map((lead) => (
                  <label
                    key={lead.id}
                    className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 ${
                      selectedLeadToAdd === lead.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="selected_lead"
                      value={lead.id}
                      checked={selectedLeadToAdd === lead.id}
                      onChange={(e) => setSelectedLeadToAdd(lead.id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {lead.field_data?.full_name || t('unnamedLead') || 'Unnamed Lead'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lead.field_data?.phone || '-'} • {lead.field_data?.email || '-'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseAddLead}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleAddLeadToEvent} disabled={!selectedLeadToAdd}>
              {t('add') || 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
